/**
 * Dropdown for selecting saved filter presets.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useEffect, useRef, useState, type FC } from 'react';
import { ChevronDown, Edit, Share2, Trash2 } from 'lucide-react';

import { PresetColorSwatch } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { SavedFilter } from '@/features/smswiser-campaigns/types/saved-filter.types';

interface PresetDropdownProps {
  presets: SavedFilter[];
  activePresetId: string | null;
  onLoad: (preset: SavedFilter) => void;
  onEdit: (preset: SavedFilter) => void;
  onDelete: (id: string) => void;
  onShare: (preset: SavedFilter) => void;
}

export const PresetDropdown: FC<PresetDropdownProps> = ({
  presets, activePresetId, onLoad, onEdit, onDelete, onShare,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const active = presets.find((p) => p.id === activePresetId);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (presets.length === 0) return null;

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen((v) => !v)} data-testid="preset-dropdown-trigger"
        className={cn(
          'flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition-colors hover:bg-accent',
          active ? 'border-primary/30 bg-primary/5 text-primary' : 'border-border/50 text-muted-foreground',
        )}>
        {active && <PresetColorSwatch color={active.color} />}
        <span className="max-w-[100px] truncate">{active?.name ?? 'Presets'}</span>
        <ChevronDown className="h-3 w-3 shrink-0 opacity-60" />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-[210] mt-1 min-w-[200px] rounded-md border bg-popover p-1 shadow-lg">
          {presets.map((p) => (
            <PresetRow key={p.id} preset={p} isActive={activePresetId === p.id}
              onLoad={() => { onLoad(p); setOpen(false); }}
              onEdit={() => { onEdit(p); setOpen(false); }}
              onDelete={() => { onDelete(p.id); setOpen(false); }}
              onShare={() => { onShare(p); setOpen(false); }} />
          ))}
        </div>
      )}
    </div>
  );
};

interface PresetRowProps {
  preset: SavedFilter;
  isActive: boolean;
  onLoad: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onShare: () => void;
}

const PresetRow: FC<PresetRowProps> = ({ preset, isActive, onLoad, onEdit, onDelete, onShare }) => (
  <div className={cn('group flex items-center gap-2 rounded-md px-2 py-1.5', isActive ? 'bg-primary/5' : 'hover:bg-muted/50')}>
    <button onClick={onLoad} className="flex min-w-0 flex-1 items-center gap-2">
      <PresetColorSwatch color={preset.color} />
      <span className="truncate text-xs font-medium">{preset.name}</span>
    </button>
    {preset.isOwner && (
      <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
        <ActionBtn icon={Edit} onClick={onEdit} label="Edit" />
        <ActionBtn icon={Share2} onClick={onShare} label="Share" />
        <ActionBtn icon={Trash2} onClick={onDelete} label="Delete" destructive />
      </div>
    )}
    {!preset.isOwner && (
      <span className="shrink-0 text-[10px] text-muted-foreground/50">shared</span>
    )}
  </div>
);

const ActionBtn: FC<{ icon: React.ElementType; onClick: () => void; label: string; destructive?: boolean }> = ({
  icon: Icon, onClick, label, destructive,
}) => (
  <button onClick={(e) => { e.stopPropagation(); onClick(); }} aria-label={label}
    className={cn('rounded p-1 text-muted-foreground/60 transition-colors hover:bg-muted', destructive ? 'hover:text-destructive' : 'hover:text-primary')}>
    <Icon className="h-3 w-3" />
  </button>
);
